<script lang="ts" setup>
import type { OfferImage } from '~/types/Catalog';

const props = defineProps<{ images: OfferImage[]; alt: string }>();

const hoveredImageIndex = ref(0);
const activeImage = computed(() => props.images[hoveredImageIndex.value]);

const { isMobile } = useBreakpoints();

function setHoveredImageIndex(index: number) {
  if (isMobile.value) return;

  hoveredImageIndex.value = index;
}

const swiper = useTemplateRef('swiper');
const containerWidth = useElementBounding(swiper).width;

const { direction, lengthX } = useSwipe(swiper, {
  threshold: 20,
  onSwipeEnd() {
    if (containerWidth.value && Math.abs(lengthX.value) / containerWidth.value >= 0.2) {
      if (lengthX.value < 0 && direction.value === 'right') {
        hoveredImageIndex.value = hoveredImageIndex.value === 0 ? props.images.length - 1 : hoveredImageIndex.value - 1;
      } else if (lengthX.value > 0 && direction.value === 'left') {
        hoveredImageIndex.value = hoveredImageIndex.value === props.images.length - 1 ? 0 : hoveredImageIndex.value + 1;
      }
    }
  },
});
</script>

<template>
  <div v-if="images.length">
    <picture class="product-card-image">
      <source
        v-if="activeImage.conversions_webp.thumb_url_180"
        :srcset="`${activeImage.conversions_webp.thumb_url_180}, ${activeImage.conversions_webp.thumb_url_360} 2x`"
        media="(max-width: 640px)"
        type="image/webp"
      />

      <source
        v-if="activeImage.conversions_webp.thumb_url_360"
        :srcset="`${activeImage.conversions_webp.thumb_url_360}, ${activeImage.conversions_webp.thumb_url_740} 2x`"
        media="(min-width: 641px) and (max-width: 1023px)"
        type="image/webp"
      />

      <source
        v-if="activeImage.conversions_webp.thumb_url_180"
        :srcset="`${activeImage.conversions_webp.thumb_url_180}, ${activeImage.conversions_webp.thumb_url_360} 2x`"
        media="(min-width: 1024px)"
        type="image/webp"
      />

      <source
        v-if="activeImage.conversions.thumb_url_360"
        :srcset="activeImage.conversions.thumb_url_360"
        type="image/jpeg"
      />

      <img
        :alt
        :src="activeImage.url"
        class="w-full h-full object-contain"
        loading="lazy"
      />
    </picture>

    <div
      v-if="images.length > 1"
      ref="swiper"
      class="product-card-image__hover-pagination"
      @mouseleave="hoveredImageIndex = 0"
    >
      <div
        v-for="(image, key) in images"
        :key
        class="product-card-image__hover-pagination-bullet-wrapper"
        @mouseover="hoveredImageIndex = key"
        @click.stop="setHoveredImageIndex(key)"
      >
        <div
          :class="{ 'product-card-image__hover-pagination-bullet--active': key === hoveredImageIndex }"
          class="product-card-image__hover-pagination-bullet"
        />
      </div>
    </div>
  </div>

  <div
    v-else
    class="product-card-image"
  >
    <SvgoNoPhotoBig
      :font-controlled="false"
      class="w-16 h-16"
      filled
    />
  </div>
</template>

<style lang="scss" scoped>
.product-card-image {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  max-height: 280px;
  background-color: $white;
  aspect-ratio: 1/1;

  &__hover-pagination {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    display: flex;
    align-items: center;
    width: 100%;
    height: calc(100% + 10px);

    @include screen-lg {
      z-index: 10;
    }
  }

  &__hover-pagination-bullet-wrapper {
    display: flex;
    flex-grow: 1;
    align-items: flex-end;
    height: 100%;
    padding: 0 2px;
  }

  &__hover-pagination-bullet {
    flex-grow: 1;
    height: 2px;
    border-radius: 1px;
    background-color: $hell-black-20;
    transition: background-color 0.1s;

    &--active {
      background-color: $hell-black-80;
    }
  }
}
</style>
